import {
  Box,
  Button,
  Checkbox,
  Chip,
  Divider,
  Grid,
  MenuItem,
  Paper,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import React, { useEffect, useState } from 'react';
import LinearProgress from '@mui/material/LinearProgress';
import { DemoContainer } from '@mui/x-date-pickers/internals/demo';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';
import { useDispatch, useSelector } from 'react-redux';
import {
  getProspectLeadsTable,
  getProspectLeadStatuses,
  updateProspectLeadStatuses,
} from '../../../redux/features/globalSlice';
import { FormikProvider, useFormik, Form } from 'formik';
import * as Yup from 'yup';
import dayjs from 'dayjs';
import useHandleApiResponse from '../../../Hooks/useHandleApiResponse';
import { CancelButton, UpdateButton } from '../../../components/mui/Buttons/Buttons';

const STATUS_DATE_PICKER = [18, 11, 12, 17, 21];

const LeadStatusUpdate = ({ row, onClose }) => {
  const dispatch = useDispatch();
  const { leadStatuses, loading } = useSelector((state) => state.global);
  const handleApiResponse = useHandleApiResponse();
  // useEffect(() => {
  //   if (leadStatuses.length === 0) {
  //     dispatch(getProspectLeadStatuses());
  //   }
  // }, [dispatch]);

  const [showConfirmBox, setShowConfirmBox] = useState(false);
  const [enrolledLeads, setEnrolledLeads] = useState([]);
  const [showEnrolledBox, setShowEnrolledBox] = useState(false);
  const [existingLeads, setExistingLeads] = useState([]);
  const [apiMessage, setApiMessage] = useState('');
  const [enrolledApiMessage, setEnrolledApiMessage] = useState('');
  const [createClicked, setCreateClicked] = useState(false);
  const [selectedLeadId, setSelectedLeadId] = useState(null);
  const initialValues = {
    statusId: row?.current_status_id || '',
    assessmentDate: row?.assessment_date ? dayjs(row.assessment_date) : null,
    remarks: '',
    leadConfirmationForCreation: null,
  };

  const validationSchema = Yup.object({
    statusId: Yup.number().required('Status is required'),
  });

  const formik = useFormik({
    enableReinitialize: true,
    initialValues,
    validationSchema,

    onSubmit: (values) => {
      const payload = {
        prospectId: row.prospect_lead_id,
        statusId: Number(values.statusId),
        assessmentDate:
          values.assessmentDate && dayjs(values.assessmentDate).isValid()
            ? dayjs(values.assessmentDate).format('YYYY-MM-DD HH:mm:ss')
            : null,

        remarks: values.remarks,
        leadConfirmationForCreation: Number(values.statusId) === 38 ? null : values.leadConfirmationForCreation,
      };
      dispatch(updateProspectLeadStatuses(payload)).then((res) => {
        const response = res?.payload; //get status data
        if (response?.data?.confirmBox) {
          setExistingLeads(response.data.existingLeads);
          setSelectedLeadId(response.data.existingLeads[0]?.lead_id);
          setApiMessage(response.message);
          setShowConfirmBox(true);
          return;
        }

        handleApiResponse(res, {
          onSuccess: () => {
            onClose(true);
          },
          onFailed: () => {
            console.log('Status update failed');
          },
        });
      });
    },
  });
  const handleCreateAsNew = () => {
    if (createClicked) return; //prevent multipple clicks
    setCreateClicked(true); //disable button permanently
    const payload = {
      prospectId: row.prospect_lead_id,
      statusId: Number(formik.values.statusId),

      assessmentDate:
        formik.values.assessmentDate && dayjs(formik.values.assessmentDate).isValid()
          ? dayjs(formik.values.assessmentDate).format('YYYY-MM-DD HH:mm:ss')
          : null,
      remarks: formik.values.remarks,
      leadConfirmationForCreation: 1,
      existingLeadId: selectedLeadId || null,
    };

    dispatch(updateProspectLeadStatuses(payload)).then((res) => {
      const response = res?.payload; //get status data
      if (response?.data?.enrolledListBox) {
        setEnrolledLeads(response?.data?.leadAllEnrollments);
        setEnrolledApiMessage(response?.message);
        setShowEnrolledBox(true);
        return;
      }
      handleApiResponse(res, {
        onSuccess: () => {
          onClose(true);
        },
      });
    });
  };

  return (
    <FormikProvider value={formik}>
      <Form onSubmit={formik.handleSubmit}>
        <Stack spacing={3} mt={2}>
          {/* STATUS */}

          <TextField
            select
            label="Status"
            name="statusId"
            value={leadStatuses.length ? formik.values.statusId : ''}
            onChange={formik.handleChange}
            fullWidth
          >
            {leadStatuses?.map((item) => (
              <MenuItem key={item.sro_lead_status_id} value={item.sro_lead_status_id}>
                {item.sro_lead_status_name}
              </MenuItem>
            ))}
          </TextField>
          {/* DATE TIME PICKER */}
          {STATUS_DATE_PICKER.includes(Number(formik.values.statusId)) && (
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <DemoContainer components={['DateTimePicker']}>
                <DateTimePicker
                  label="Assessment Date"
                  value={formik.values.assessmentDate || null}
                  onChange={(newValue) => formik.setFieldValue('assessmentDate', newValue)}
                  slotProps={{
                    textField: { fullWidth: true },
                  }}
                />
              </DemoContainer>

              <Button
                variant="outlined"
                onClick={() => {
                  formik.setFieldValue('assessmentDate', null);
                  formik.setFieldTouched('assessmentDate', false);
                }}
              >
                Clear Date
              </Button>
            </LocalizationProvider>
          )}

          {/* REMARKS */}
          <TextField
            label="Remarks"
            name="remarks"
            multiline
            rows={3}
            value={formik.values.remarks}
            onChange={formik.handleChange}
            fullWidth
          />

          {showConfirmBox && existingLeads.length > 0 && (
            <Paper
              elevation={3}
              sx={{
                p: 2.5,
                borderRadius: 3,
                backgroundColor: '#fafafa',
                mt: 2,
              }}
            >
              <Stack spacing={2}>
                {/* Header */}
                <Typography variant="h6" fontWeight="bold">
                  Existing Leads
                </Typography>

                {/* Message */}
                {apiMessage && (
                  <Typography color="error" fontWeight={500}>
                    {apiMessage}
                  </Typography>
                )}

                {/* Leads List */}
                {existingLeads.map((lead, index) => {
                  const isSelected = selectedLeadId === lead.lead_id;

                  return (
                    <Paper
                      key={lead.lead_id}
                      variant="outlined"
                      sx={{
                        p: 2,
                        borderRadius: 2,
                        borderColor: isSelected ? 'primary.main' : 'divider',
                        backgroundColor: isSelected ? 'action.selected' : 'white',
                        transition: 'all 0.2s ease',
                        cursor: 'pointer',
                        '&:hover': {
                          boxShadow: 3,
                        },
                      }}
                      onClick={() => setSelectedLeadId(lead.lead_id)}
                    >
                      <Grid container spacing={1} alignItems="center">
                        {/* Checkbox */}
                        <Grid item>
                          <Checkbox checked={isSelected} onChange={() => setSelectedLeadId(lead.lead_id)} />
                        </Grid>

                        <Grid item xs={12} sm={3}>
                          <Typography variant="body2">
                            <strong>Lead ID:</strong> {lead.lead_id}
                          </Typography>
                        </Grid>

                        <Grid item xs={12} sm={3}>
                          <Typography variant="body2">
                            <strong>Name:</strong> {lead.lead_name}
                          </Typography>
                        </Grid>

                        <Grid item xs={12} sm={3}>
                          <Typography variant="body2">
                            <strong>Mobile:</strong> {lead.lead_mob_no}
                          </Typography>
                        </Grid>

                        {/* <Grid item xs={12} sm={3}>
                          <Typography variant="body2">
                            <strong>Email:</strong> {lead.lead_email_id}
                          </Typography>
                        </Grid> */}
                      </Grid>

                      {index !== existingLeads.length - 1 && <Divider sx={{ mt: 2 }} />}
                    </Paper>
                  );
                })}
              </Stack>
            </Paper>
          )}

          {showEnrolledBox && enrolledLeads.length > 0 && (
            <Paper
              elevation={3}
              sx={{
                p: 2.5,
                borderRadius: 3,
                backgroundColor: '#fafafa',
              }}
            >
              <Stack spacing={2}>
                {/* Header */}
                <Typography variant="h6" fontWeight="bold">
                  Enrolled Active Courses
                </Typography>

                {/* Message */}
                {enrolledApiMessage && (
                  <Typography color="error" fontWeight={500}>
                    {enrolledApiMessage}
                  </Typography>
                )}

                {/* Leads List */}
                {enrolledLeads.map((lead, index) => (
                  <Paper
                    key={lead.lead_id}
                    variant="outlined"
                    sx={{
                      p: 2,
                      borderRadius: 2,
                      transition: 'all 0.2s ease',
                      '&:hover': {
                        boxShadow: 3,
                        borderColor: 'primary.main',
                      },
                    }}
                  >
                    <Grid container spacing={1}>
                      <Grid item xs={12} sm={6}>
                        <Typography variant="body2">
                          <strong>Lead ID:</strong> {lead.lead_id}
                        </Typography>
                      </Grid>

                      <Grid item xs={12} sm={6}>
                        <Typography variant="body2">
                          <strong>Admission No:</strong> {lead.admn_no}
                        </Typography>
                      </Grid>

                      <Grid item xs={12} sm={6}>
                        <Typography variant="body2">
                          <strong>Name:</strong> {lead.lead_name}
                        </Typography>
                      </Grid>

                      <Grid item xs={12} sm={6}>
                        <Typography variant="body2">
                          <strong>Mobile:</strong> {lead.lead_country_code} {lead.lead_mob_no}
                        </Typography>
                      </Grid>

                      <Grid item xs={12} sm={6}>
                        <Typography variant="body2">
                          <strong>Email:</strong> {lead.lead_email_id}
                        </Typography>
                      </Grid>

                      <Grid item xs={12} sm={6}>
                        <Typography variant="body2">
                          <strong>Lead Owner:</strong> {lead.sro_name}
                        </Typography>
                      </Grid>

                      <Grid item xs={12} sm={6}>
                        <Typography variant="body2">
                          <strong>CRO Name:</strong> {lead.cro_name}
                        </Typography>
                      </Grid>

                      <Grid item xs={12} sm={6}>
                        <Stack direction="row" spacing={1} alignItems="center">
                          <Typography variant="body2">
                            <strong>Course Status:</strong>
                          </Typography>

                          <Chip
                            label={lead.course_enrollment_status == 1 ? 'Active' : 'Inactive'}
                            color={lead.course_enrollment_status == 1 ? 'success' : 'default'}
                            size="small"
                            variant="filled"
                          />
                        </Stack>
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <Typography variant="body2">
                          <strong>Course Name:</strong> {lead.course_name}
                        </Typography>
                      </Grid>
                    </Grid>

                    {/* Divider between cards */}
                    {index !== enrolledLeads.length - 1 && <Divider sx={{ mt: 2 }} />}
                  </Paper>
                ))}
              </Stack>
            </Paper>
          )}
          {loading && (
            <Box sx={{ width: '100%' }}>
              <LinearProgress />
            </Box>
          )}
          {/* SUBMIT */}
          <Stack direction="row" spacing={2} justifyContent="flex-end">
            <CancelButton action={() => onClose(false)}>Cancel</CancelButton>

            {!showConfirmBox ? (
              <UpdateButton disabled={loading} type="submit">
                {loading ? 'Updating...' : 'Update'}
              </UpdateButton>
            ) : (
              <Button
                variant="contained"
                color="primary"
                onClick={handleCreateAsNew}
                disabled={loading || enrolledLeads.length > 0}
              >
                {existingLeads.length > 0 ? 'Transfer Ownership' : 'Submit'}
              </Button>
            )}
          </Stack>
        </Stack>
      </Form>
    </FormikProvider>
  );
};

export default LeadStatusUpdate;
